@if (isLoading()) {
  <ziro-loading-block></ziro-loading-block>
} @else {
  @if (!!autoAttendantView()) {
    <ziro-collapsible-section-card
      data-automation="general-settings-form"
      [isSwitchHidden]="true"
      [isCollapsible]="false"
      [title]="'tkey;admin.teams_voice_management.teams_auto_attendant.general_settings.title.label'"
      [subtext]="'tkey;admin.teams_voice_management.teams_auto_attendant.general_settings.title.helptext'"
      [icon]="ZiroIcons.GEAR_COMPLEX"
    >
      <div class="row">
        <div class="col-md-12">
          <div class="row">
            <div class="col-md-6">
              <ziro-text [fieldId]="'name'" [inlineLabel]="false"></ziro-text>
            </div>
            <div class="col-md-6">
              <ziro-select [fieldId]="'resourceAccounts'" [inlineLabel]="false"></ziro-select>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <ziro-select [fieldId]="'timezone'" [inlineLabel]="false"></ziro-select>
            </div>
            <div class="col-md-6">
              <ziro-select [fieldId]="'language'" [inlineLabel]="false"></ziro-select>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <ziro-select [fieldId]="'operatorType'" [inlineLabel]="false"></ziro-select>
            </div>
            <div class="col-md-6">
              <ziro-text
                [fieldId]="'destinationNumber'"
                [inlineLabel]="false"
                [hidden]="formData.operatorType?.value !== AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER"
              ></ziro-text>
              <ziro-select [fieldId]="'destination'" [inlineLabel]="false"></ziro-select>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <label class="col-form-label col-12 pb-0">
                {{ 'tkey;admin.teams_voice_management.teams_auto_attendant.authorized_users.label' | translate }}
              </label>
              <div class="d-flex align-items-center gap-2">
                <ziro-button
                  [class]="'me-2 flex-shrink-0'"
                  [dataAutomation]="'auto-attendant-authorized-users-edit-button'"
                  [label]="'tkey;admin.teams_voice_management.teams_auto_attendant.edit_button.label'"
                  [isDisabled]="isSubmitting"
                  [icon]="ZiroIcons.EDIT"
                  [buttonSize]="ButtonSizes.SMALL"
                  [buttonStyle]="ButtonStyles.OUTLINE_DEFAULT"
                  (click)="openAuthorizedUsersModal()"
                ></ziro-button>

                @if (localAuthorizedUsers().length) {
                  <div class="d-flex align-items-center flex-shrink-0">
                    @for (user of localAuthorizedUsers().slice(0, 3); track user.guid) {
                      <div
                        class="me-2"
                        [ngbTooltip]="user.displayName"
                        [attr.data-automation]="'authorized-user-avatar-' + user.guid"
                      >
                        @if (user.photoBase64) {
                          <img [src]="'data:image/png;base64,' + user.photoBase64" class="authorized-user-pic" />
                        } @else if (user.displayName) {
                          <span class="initials initials-letterspacing-2">
                            <strong>{{ getUserInitials(user.displayName) }}</strong>
                          </span>
                        } @else {
                          <i class="fa fa-2x display-pic-size no-user-icon" [class]="ZiroIcons.CIRCLE_USER"></i>
                        }
                      </div>
                    }
                  </div>
                } @else {
                  <span class="text-muted">
                    {{
                      'tkey;admin.teams_voice_management.teams_auto_attendant.authorized_users.no_users_configured'
                        | translate
                    }}
                  </span>
                }
              </div>
            </div>
            <div class="col-md-6">
              <label class="col-form-label col-12 pb-0">
                {{ 'tkey;admin.teams_voice_management.teams_auto_attendant.dial_scope.label' | translate }}
              </label>
              <div class="d-flex align-items-center gap-2">
                <ziro-button
                  [class]="'me-2 flex-shrink-0'"
                  [dataAutomation]="'auto-attendant-dial-scope-edit-button'"
                  [label]="'tkey;admin.teams_voice_management.teams_auto_attendant.edit_button.label'"
                  [icon]="ZiroIcons.EDIT"
                  [buttonSize]="ButtonSizes.SMALL"
                  (clicked)="openDialScopeModal()"
                  [buttonStyle]="ButtonStyles.OUTLINE_DEFAULT"
                ></ziro-button>

                <span class="d-inline-flex align-items-center gap-1" data-automation="dial-scope-inclusions">
                  <i class="font-size-11 dial-scope-indicator {{ ZiroIcons.CIRCLE }} text-success"></i>
                  {{ getDialScopeInclusionLabel() }},
                </span>
                <span class="d-inline-flex align-items-center gap-1" data-automation="dial-scope-exclusions">
                  <i class="font-size-11 dial-scope-indicator {{ ZiroIcons.CIRCLE }} text-danger"></i>
                  {{ getDialScopeExclusionLabel() }}
                </span>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6" data-automation="references-section">
              <label class="col-form-label col-12 pb-0">
                {{
                  'tkey;admin.teams_voice_management.teams_auto_attendant.references'
                    | translate: { name: formData.name }
                }}
              </label>
              @if (!references().length) {
                <div class="text-muted" data-automation="references-none">
                  {{ 'tkey;admin.teams_voice_management.teams_auto_attendant.references.none' | translate }}
                </div>
              } @else {
                @for (ref of references(); track ref.id; let i = $index) {
                  <div
                    class="d-flex align-items-center justify-content-between py-2"
                    [class.border-bottom]="i < references().length - 1"
                    [attr.data-automation]="'reference-row-' + ref.id"
                    [attr.data-href]="getReferenceHref(ref)"
                  >
                    <div class="d-flex align-items-center gap-2 min-w-0">
                      <i
                        class="fa {{ getReferenceIcon(ref.type) }}"
                        [attr.data-automation]="'reference-icon-' + ref.id"
                      ></i>
                      <div class="min-w-0">
                        <div
                          class="text-truncate fw-semibold"
                          [attr.data-automation]="'reference-display-name-' + ref.id"
                        >
                          {{ ref.displayName }}
                        </div>
                        <div class="text-muted" [attr.data-automation]="'reference-type-label-' + ref.id">
                          {{ getReferenceTypeLabel(ref.type) }}
                        </div>
                      </div>
                    </div>
                    <ziro-button
                      [cssClass]="'ms-2 flex-shrink-0 btn-border'"
                      [dataAutomation]="'reference-navigate-button-' + ref.id"
                      [icon]="ZiroIcons.NEXT"
                      [buttonStyle]="ButtonStyles.OUTLINE_PRIMARY"
                      [buttonSize]="ButtonSizes.XSMALL"
                      (click)="navigateToReference(ref)"
                    ></ziro-button>
                  </div>
                }
              }
            </div>
            <div class="col-md-6">
              <ziro-checkbox [fieldId]="'voiceResponseEnabled'"></ziro-checkbox>
            </div>
          </div>
        </div>
      </div>
    </ziro-collapsible-section-card>

    <ziro-teams-auto-attendants-call-flow
      [isDisabled]="isSubmitting"
      [tenantId]="tenantId()"
      [standardCallFlows]="standardCallFlows"
      [holidayCallFlows]="holidayCallFlows"
      [allHolidays]="autoAttendantsHolidays()"
      (greetingUpdate)="onCallFlowGreetingUpdate($event)"
    ></ziro-teams-auto-attendants-call-flow>
  }
}
